import { useState } from 'react';
import { Alert, Box, Chip, Grid, Typography } from '@mui/material';
import IngredientSelector from '../components/Meal/Ingredient/IngredientSelector';
import { callApiWithData } from '../api/call-api';
import { FilteredMeal, mapMealFromApi } from '../models/meal';
import MealItem from '../components/Meal/MealItem';
import Loader from '../components/UI/General/Loader';

const CookWithWhatIHave = () => {
  const [filteredMeals, setFilteredMeals] = useState<FilteredMeal[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async (ingredientIds: string[]) => {
    setLoading(true);
    setSearched(true);
    setError(null);
    try {
      const response = await callApiWithData(
        '/chef-meals',
        {
          ingredients: ingredientIds,
          sort: 'ingredient-match',
        },
        'POST'
      );

      const meals: FilteredMeal[] = response.meals.map((meal: any) => ({
        ...mapMealFromApi(meal),
        matchedCount: meal.matchedCount ?? 0,
        totalIngredientCount: meal.totalIngredientCount ?? 0,
      }));

      setFilteredMeals(meals);
    } catch {
      setFilteredMeals([]);
      setError('Nepodařilo se vyhledat recepty. Zkuste to prosím znovu.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Vařit z toho, co mám
      </Typography>

      <Box sx={{ mb: 3 }}>
        <IngredientSelector onSearch={handleSearch} loading={loading} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && <Loader />}

      {!loading && searched && filteredMeals.length === 0 && !error && (
        <Typography variant="body1" color="text.secondary">
          Žádné recepty neodpovídají vybraným ingrediencím.
        </Typography>
      )}

      {!loading && !searched && (
        <Typography variant="body1" color="text.secondary">
          Vyber ingredience, které máš doma, a najdi recepty.
        </Typography>
      )}

      {!loading && filteredMeals.length > 0 && (
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {filteredMeals.map((meal) => (
            <Grid key={meal.id} item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                <MealItem meal={meal} />
                {meal.totalIngredientCount > 0 && (
                  <Chip
                    label={`${meal.matchedCount}/${meal.totalIngredientCount} ingrediencí`}
                    color={meal.matchedCount === meal.totalIngredientCount ? 'success' : 'default'}
                    size="small"
                    sx={{ position: 'absolute', top: 8, right: 48 }}
                  />
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default CookWithWhatIHave;
